// OidcLoginPage.tsx — drop-in <Route>'s element for /login.
//
// Pairs with the server-side lib/oidcrp handler, and picks one of two shapes
// based on what that backend says it supports:
//
//   - Embedded (preferred). The backend accepts credentials directly, so we
//     render LoginForm right here and the user signs in without ever leaving
//     this origin.
//   - Redirect (fallback). The backend can't take a password — embedded login
//     is off, or this client isn't allowlisted at the issuer — so we kick the
//     browser at /api/oidc/login, which runs the authz round-trip and comes
//     back with the session cookie set.
//
// The choice is a runtime probe of /api/oidc/config rather than a build-time
// flag, so turning embedded login on or off is a server-side decision that
// takes effect on the next page load. A backend that doesn't serve the config
// route at all (an older deployment) reads as "redirect", which is exactly
// what it used to do.
//
// Loop detection: if /api/me keeps returning 401 even after a fresh
// OIDC dance (typical cause: the issued JWT is missing the scope this
// service requires), the SPA bounces ←→ /login ←→ /api/oidc/login
// ←→ authz SSO session ←→ callback ←→ / ←→ 401 forever. We track
// recent attempts in sessionStorage and break out with a visible error
// after the third rapid redirect, so the user sees *why* they're stuck
// and can clear the session instead of watching the URL bar flicker.
// Only the redirect path can loop — the embedded form stays on the page and
// reports failures inline — so the counter is checked there.
import { useEffect, useState } from 'react';
import { LoginForm } from './LoginForm';
import { useLoginConfig } from './useLoginConfig';

export interface OidcLoginPageProps {
    /** Server-side route that starts the OAuth code flow. Default "/api/oidc/login". */
    loginPath?: string;
    /** Where the SPA should land after a successful login. Default "/". */
    returnTo?: string;
    /** Query-parameter name the backend reads for the return path. Default "return_to". */
    returnParam?: string;
    /** Issuer label shown in the "Signing in via …" message. */
    issuerLabel?: string;
    /** Server-side logout endpoint used by the loop-break error UI. Default "/api/oidc/logout". */
    logoutPath?: string;
    /** Route describing what sign-in this backend supports. Default "/api/oidc/config". */
    configPath?: string;
    /** Route that accepts credentials directly. Default "/api/oidc/password-login". */
    passwordLoginPath?: string;
    /** Title on the embedded sign-in card. Default "Log In". */
    title?: string;
    /**
     * Force the embedded form on or off instead of asking the backend.
     * `false` restores the pure-redirect behavior unconditionally.
     */
    embedded?: boolean;
    /** Called instead of navigating after an embedded sign-in succeeds. */
    onSuccess?: (returnTo: string) => void;
}

const LOOP_STORAGE_KEY = 'oidcLoginAttempts';
const LOOP_WINDOW_MS = 15_000;
const LOOP_LIMIT = 3;

export function OidcLoginPage(props: OidcLoginPageProps) {
    const loginPath = props.loginPath ?? '/api/oidc/login';
    const returnTo = isSafeReturnPath(props.returnTo) ? props.returnTo! : '/';
    const returnParam = props.returnParam ?? 'return_to';
    const logoutPath = props.logoutPath ?? '/api/oidc/logout';
    const passwordLoginPath = props.passwordLoginPath ?? '/api/oidc/password-login';
    const [looping, setLooping] = useState(false);

    const config = useLoginConfig(props.configPath);
    // An explicit `embedded` prop overrides the probe, but the probe still
    // runs — its providers and issuer label are useful either way.
    const embedded = props.embedded ?? config.password;
    const issuerLabel = props.issuerLabel ?? config.issuer;

    // Redirect only once we know there's no embedded form to render. Keeping
    // this separate from the probe keeps the loop counter honest: it advances
    // per redirect actually issued, not per mount.
    useEffect(() => {
        if (config.loading || embedded) return;
        const stamps = peekLoopStamps(loginPath);
        console.info(`[oidc] /login mounted; recent stamps=${stamps.length} (limit=${LOOP_LIMIT}, window=${LOOP_WINDOW_MS}ms)`);
        if (detectLoop(loginPath)) {
            console.error(
                `[oidc] Detected ${LOOP_LIMIT}+ login redirects within ${LOOP_WINDOW_MS}ms. ` +
                `Likely cause: /api/me keeps 401-ing (missing scope on the issued JWT, or ` +
                `cookie not being accepted). Stopping the loop.`,
            );
            setLooping(true);
            return;
        }
        const url = `${loginPath}?${returnParam}=${encodeURIComponent(returnTo)}`;
        console.info(`[oidc] Redirecting to ${url}`);
        window.location.replace(url);
    }, [config.loading, embedded, loginPath, returnParam, returnTo]);

    if (looping) {
        return (
            <main>
                <div className="alert alert-danger" role="alert">
                    <strong>Sign-in loop detected.</strong>{' '}
                    You authenticated at <code>{issuerLabel}</code> but this service
                    rejected the resulting session. The most common cause is a missing
                    scope on the issued JWT — ask an admin to grant your account access
                    to this service, then{' '}
                    <a
                        href={logoutPath}
                        onClick={() => {
                            try { sessionStorage.removeItem(LOOP_STORAGE_KEY); } catch { /* ignore */ }
                        }}
                    >
                        clear the session
                    </a>{' '}
                    and try again.
                </div>
            </main>
        );
    }

    if (!config.loading && embedded) {
        return (
            <LoginForm
                title={props.title}
                returnTo={returnTo}
                loginPath={loginPath}
                passwordLoginPath={passwordLoginPath}
                returnParam={returnParam}
                providers={config.providers}
                issuerLabel={issuerLabel}
                onSuccess={props.onSuccess}
            />
        );
    }

    // Probing, or redirecting. Both are a brief blank moment; the manual link
    // is there for the case where the redirect doesn't fire.
    const manualUrl = `${loginPath}?${returnParam}=${encodeURIComponent(returnTo)}`;
    return (
        <main>
            <div className="empty-state">
                <div>Signing in via <code>{issuerLabel}</code>&hellip;</div>
                <div style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
                    Not redirected automatically? <a href={manualUrl}>Click here to sign in</a>.
                </div>
            </div>
        </main>
    );
}

// peekLoopStamps returns the un-mutated current stamp list for diagnostics.
// Filters out stamps older than LOOP_WINDOW_MS so the count reflects what
// detectLoop will see.
function peekLoopStamps(loginPath: string): number[] {
    if (typeof sessionStorage === 'undefined') return [];
    const now = Date.now();
    try {
        const raw = sessionStorage.getItem(LOOP_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as Record<string, number[]>;
        return (parsed[loginPath] ?? []).filter((t) => now - t < LOOP_WINDOW_MS);
    } catch {
        return [];
    }
}

// detectLoop records a timestamp for this redirect attempt in sessionStorage
// and returns true if LOOP_LIMIT attempts have happened within LOOP_WINDOW_MS.
// Keyed by loginPath so multiple SPAs sharing an origin don't poison each
// other's counters.
function detectLoop(loginPath: string): boolean {
    if (typeof sessionStorage === 'undefined') return false;
    const now = Date.now();
    let stamps: number[] = [];
    try {
        const raw = sessionStorage.getItem(LOOP_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as Record<string, number[]>;
            stamps = (parsed[loginPath] ?? []).filter((t) => now - t < LOOP_WINDOW_MS);
        }
    } catch {
        stamps = [];
    }
    stamps.push(now);
    try {
        const raw = sessionStorage.getItem(LOOP_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) as Record<string, number[]> : {};
        parsed[loginPath] = stamps;
        sessionStorage.setItem(LOOP_STORAGE_KEY, JSON.stringify(parsed));
    } catch {
        /* ignore — sessionStorage may be disabled */
    }
    return stamps.length >= LOOP_LIMIT;
}

export default OidcLoginPage;

/**
 * startOidcLogin imperatively triggers the same redirect from a handler
 * (e.g. an onClick on a "Sign in" button) — useful when the SPA wants
 * a button on its public landing page instead of an auto-redirect.
 */
export function startOidcLogin(opts: OidcLoginPageProps = {}): void {
    const loginPath = opts.loginPath ?? '/api/oidc/login';
    const returnTo = isSafeReturnPath(opts.returnTo) ? opts.returnTo! : '/';
    const returnParam = opts.returnParam ?? 'return_to';
    window.location.href = `${loginPath}?${returnParam}=${encodeURIComponent(returnTo)}`;
}

function isSafeReturnPath(p: string | undefined): boolean {
    if (!p) return false;
    if (!p.startsWith('/')) return false;
    if (p.startsWith('//')) return false;
    return true;
}
